"use client";
import { cva, type VariantProps } from "class-variance-authority";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CustomButton from "./customButton";

const toastVariants = cva(
  "fixed z-[9999] p-4 rounded-lg text-sm shadow-lg cursor-default flex gap-4 items-center justify-between min-w-[300px]",
  {
    variants: {
      variant: {
        default: "bg-primary-main border border-white text-white",
        success: "bg-emerald-mint text-white",
        error: "bg-error-dark text-white",
        outline: "bg-white border-2 border-black text-gray-900",
      },
      position: {
        "top-center": "top-4 left-1/2 -translate-x-1/2",
        "bottom-center": "bottom-4 left-1/2 -translate-x-1/2",
        "top-left": "top-4 left-4",
        "top-right": "top-4 right-4",
        "bottom-left": "bottom-4 left-4",
        "bottom-right": "bottom-4 right-4",
      },
    },
    defaultVariants: {
      variant: "success",
      position: "top-center",
    },
  },
);

interface ToastProps extends VariantProps<typeof toastVariants> {
  message: string;
  onClose?: () => void;
}

const Toast = ({ message, variant, position, onClose }: ToastProps) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      onClose?.();
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const getAnimationVariants = () => {
    const positionClasses = toastVariants({ variant, position }).split(" ");
    const hasTop = positionClasses.some((p) => p.includes("top"));
    const hasBottom = positionClasses.some((p) => p.includes("bottom"));

    return {
      initial: {
        opacity: 0,
        y: hasTop ? -100 : hasBottom ? 100 : 0,
      },
      animate: {
        opacity: 1,
        y: 0,
      },
      exit: {
        opacity: 0,
        y: hasTop ? -100 : hasBottom ? 100 : 0,
      },
    };
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={toastVariants({ variant, position })}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={getAnimationVariants()}
          transition={{ duration: 0.3 }}
        >
          <span className="grow">{message}</span>
          <CustomButton
            id="toast-close"
            onClick={() => {
              setIsVisible(false);
              onClose?.();
            }}
            className="w-max bg-black px-4! py-1! text-sm! shadow-none!"
          >
            بستن
          </CustomButton>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Toast;
